import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Step } from 'react-joyride';

type OnboardingContextValue = {
  isRunning: boolean;
  stepIndex: number;
  steps: Step[];
  start: (steps?: Step[]) => void;
  stop: () => void;
  setSteps: (steps: Step[]) => void;
  setStepIndex: (index: number) => void;
};

export const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined);

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider');
  return ctx;
}

export function useOnboardingState(initialSteps: Step[] = []) {
  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState<Step[]>(initialSteps);

  const start = useCallback((maybeSteps?: Step[]) => {
    if (maybeSteps && maybeSteps.length > 0) setSteps(maybeSteps);
    setStepIndex(0);
    setIsRunning(true);
  }, []);

  const stop = useCallback(() => {
    setIsRunning(false);
  }, []);

  return useMemo(
    () => ({ isRunning, stepIndex, steps, start, stop, setSteps, setStepIndex }),
    [isRunning, stepIndex, steps, start, stop]
  );
}
